import * as Icons from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';

// Alle verfügbaren Material-UI Icons
const ALL_ICONS = Object.keys(Icons).filter((key) => key !== 'default');

// Kategorien für bessere Organisation
const ICON_CATEGORIES = {
    common: ['Home', 'Person', 'Work', 'School', 'Assignment', 'Description', 'Settings', 'Info', 'Help'],
    action: ['Add', 'Delete', 'Edit', 'Save', 'Cancel', 'Done', 'Check', 'Close', 'Search', 'Refresh'],
    communication: ['Email', 'Phone', 'Chat', 'Message', 'Call', 'VideoCall', 'Forum', 'Comment'],
    content: ['Create', 'ContentCopy', 'ContentCut', 'ContentPaste', 'Archive', 'Inbox', 'Send', 'Draft'],
    file: ['Folder', 'InsertDriveFile', 'CloudUpload', 'CloudDownload', 'AttachFile', 'PictureAsPdf'],
    navigation: ['Menu', 'ArrowBack', 'ArrowForward', 'ExpandMore', 'ExpandLess', 'ChevronLeft', 'ChevronRight'],
    social: ['Share', 'ThumbUp', 'ThumbDown', 'Favorite', 'Star', 'Group', 'Person', 'Public'],
    toggle: ['CheckBox', 'CheckBoxOutlineBlank', 'RadioButtonChecked', 'RadioButtonUnchecked', 'ToggleOn', 'ToggleOff'],
};

export interface IconPickerProps {
    open: boolean;
    currentIcon?: string;
    onClose: () => void;
    onSelect: (iconName: string) => void;
}

export const IconPicker: React.FC<IconPickerProps> = ({ open, currentIcon, onClose, onSelect }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIcon, setSelectedIcon] = useState(currentIcon || '');
    const [selectedCategory, setSelectedCategory] = useState('common');

    const filteredIcons = useMemo(() => {
        if (searchTerm) {
            return ALL_ICONS.filter((iconName) => iconName.toLowerCase().includes(searchTerm.toLowerCase())).slice(
                0,
                100,
            ); // Limit für Performance
        }
        return ICON_CATEGORIES[selectedCategory as keyof typeof ICON_CATEGORIES] || [];
    }, [searchTerm, selectedCategory]);

    const handleSelect = (iconName: string) => {
        setSelectedIcon(iconName);
    };

    const handleConfirm = () => {
        onSelect(selectedIcon);
        onClose();
    };

    const renderIcon = (iconName: string) => {
        const IconComponent = (Icons as any)[iconName];
        return IconComponent ? <IconComponent /> : null;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Icon auswählen</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    label="Icon suchen (z.B. 'home', 'person', 'work')"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 2 }}
                    placeholder="Suchen Sie aus über 2000 Material-UI Icons..."
                />

                {!searchTerm && (
                    <Tabs
                        value={selectedCategory}
                        onChange={(_, value) => setSelectedCategory(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ mb: 2 }}
                    >
                        <Tab label="Häufig" value="common" />
                        <Tab label="Aktionen" value="action" />
                        <Tab label="Kommunikation" value="communication" />
                        <Tab label="Inhalt" value="content" />
                        <Tab label="Dateien" value="file" />
                        <Tab label="Navigation" value="navigation" />
                        <Tab label="Social" value="social" />
                        <Tab label="Toggle" value="toggle" />
                    </Tabs>
                )}

                {selectedIcon && (
                    <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Ausgewähltes Icon:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {renderIcon(selectedIcon)}
                            <Typography variant="body2">{selectedIcon}</Typography>
                        </Box>
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        maxHeight: 400,
                        overflowY: 'auto',
                        p: 1,
                    }}
                >
                    {filteredIcons.map((iconName) => (
                        <IconButton
                            key={iconName}
                            onClick={() => handleSelect(iconName)}
                            sx={{
                                border: selectedIcon === iconName ? 2 : 1,
                                borderColor: selectedIcon === iconName ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                p: 1,
                                minWidth: 48,
                                minHeight: 48,
                            }}
                            title={iconName}
                        >
                            {renderIcon(iconName)}
                        </IconButton>
                    ))}
                </Box>

                {searchTerm && filteredIcons.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        Keine Icons gefunden für &quot;{searchTerm}&quot;
                    </Typography>
                )}

                {searchTerm && filteredIcons.length === 100 && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'center', display: 'block', mt: 1 }}
                    >
                        Nur die ersten 100 Ergebnisse werden angezeigt. Verfeinern Sie Ihre Suche.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abbrechen</Button>
                <Button onClick={handleConfirm} variant="contained" disabled={!selectedIcon}>
                    Auswählen
                </Button>
            </DialogActions>
        </Dialog>
    );
};
